import React, { useCallback, useState, useEffect } from "react";
import {
  Image,
  Form,
  Checkbox,
  Button,
  Icon,
  Dropdown,
  Card,
  Modal,
} from "semantic-ui-react";
import { useFormik } from "formik";
import { initialValues, validationSchema } from "./TeacherForm.form";
import { TeacherAPI } from "../../../../api";
import { InstitutionApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { useDropzone } from "react-dropzone";

const teacherApi = new TeacherAPI();
const institutionController = new InstitutionApi();

export function TeacherForm(props) {
  const { onClose, onReload, teacher } = props;
  const { accessToken, user } = useAuth();
  const institutionId = user.user.institutions[0];

  const [institution, setInstitution] = useState(null);
  // Estado para mostrar/ocultar el modal de límite
  const [showLimitModal, setShowLimitModal] = useState(false);

  useEffect(() => {
    const fetchInstitution = async () => {
      try {
        const response = await institutionController.getInstitutionsbyInstitution(
          accessToken,
          institutionId,
          true
        );
        setInstitution(response.institution);
        //console.log("Institución:", response.institution);
      } catch (error) {
        console.error(error);
      }
    };
    fetchInstitution();
  }, [accessToken, institutionId]);

  const classNumberOptions = (institution?.classnumber || []).map((cn) => ({
    key: cn,
    value: cn,
    text: cn,
  }));

  const roleOptions = [
    { key: "docente", value: "Docente", text: "Docente" },
    { key: "tutor", value: "Tutor/a", text: "Tutor/a" },
    { key: "jefeestudio", value: "Jefe/a de Estudio", text: "Jefe/a de Estudio" },
    { key: "director", value: "Director/a", text: "Director/a" },
  ];

  // Estado local para almacenar la lista de classrole
  const [classroleList, setClassroleList] = useState(teacher?.classrole || []);
  const [selectedClassnumber, setSelectedClassnumber] = useState("");
  const [selectedRole, setSelectedRole] = useState("");

  const formik = useFormik({
    initialValues: initialValues(teacher),
    validationSchema: validationSchema(teacher),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        // 1. Verificar el límite SOLO si es creación (teacher no existe)
        if (!teacher && institution) {
          const currentTeachersCount = institution?.teachers?.length || 0;
          const maxTeachersAllowed = institution?.teachersnum || 0;

          // Si ya se alcanzó el límite, mostramos modal y cancelamos
          if (currentTeachersCount >= maxTeachersAllowed) {
            setShowLimitModal(true);
            return; // Detener la ejecución, no crear ni actualizar
          }
        }

        // 2. Construir formData con los datos del formulario
        const formData = new FormData();
        formData.append("firstname", formValue.firstname);
        formData.append("lastname", formValue.lastname);
        formData.append("email", formValue.email);

        if (formValue.password) {
          formData.append("password", formValue.password);
        }
        formData.append("active", formValue.active);

        // Enviar classrole como JSON
        formData.append("classrole", JSON.stringify(classroleList));

        // Avatar si se ha seleccionado uno nuevo
        if (formValue.fileAvatar) {
          formData.append("avatar", formValue.fileAvatar);
        }

        // 3. Crear o actualizar
        if (!teacher) {
          await teacherApi.createTeacher(accessToken, formData, institutionId);
        } else {
          await teacherApi.updateTeacher(accessToken, teacher._id, formData);
        }

        onReload();
        onClose();
      } catch (error) {
        console.error("Error during teacher submission:", error);
      }
    },
  });

  // Dropzone para subir avatar
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const validMimeTypes = ["image/jpeg", "image/png"];
        if (validMimeTypes.includes(file.type)) {
          formik.setFieldValue("avatar", URL.createObjectURL(file));
          formik.setFieldValue("fileAvatar", file);
        } else {
          console.error("Formato de archivo inválido. Debe ser JPG o PNG.");
        }
      }
    },
    [formik]
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    onDrop,
    multiple: false,
  });

  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return formik.values.avatar;
    }
    return image.noAvatar;
  };

  // Manejo de classrole
  const handleAddClassrole = () => {
    if (!selectedClassnumber || !selectedRole) {
      console.error("Debe seleccionar una clase y un rol antes de añadir.");
      return;
    }
    const newEntry = { classnumber: selectedClassnumber, role: selectedRole };
    setClassroleList([...classroleList, newEntry]);
    setSelectedClassnumber("");
    setSelectedRole("");
  };

  const handleDeleteClassrole = (index) => {
    const newList = classroleList.filter((_, i) => i !== index);
    setClassroleList(newList);
  };

  // Modal de aviso de límite
  // (Se muestra solo cuando se intenta crear un docente nuevo
  //  y se ha superado el número máximo)
  const LimitReachedModal = (
    <Modal open={showLimitModal} onClose={() => setShowLimitModal(false)} size="small">
      <Modal.Header>Límite de Docentes Alcanzado</Modal.Header>
      <Modal.Content>
        <p>Has alcanzado el límite de docentes que tu plan actual permite.</p>
        <p>Para crear más docentes, necesitas actualizar tu plan o eliminar alguno existente.</p>
      </Modal.Content>
      <Modal.Actions>
        <Button primary onClick={() => setShowLimitModal(false)}>
          Cerrar
        </Button>
      </Modal.Actions>
    </Modal>
  );

  return (
    <>
      {LimitReachedModal}

      <Form className="user-form" onSubmit={formik.handleSubmit}>
        <div className="user-form__avatar" {...getRootProps()}>
          <input {...getInputProps()} />
          <Image avatar size="small" src={getAvatar()} />
        </div>

        <Form.Group widths="equal">
          <Form.Input
            name="firstname"
            placeholder="Nombre"
            onChange={formik.handleChange}
            value={formik.values.firstname}
            error={formik.errors.firstname}
          />
          <Form.Input
            name="lastname"
            placeholder="Apellidos"
            onChange={formik.handleChange}
            value={formik.values.lastname}
            error={formik.errors.lastname}
          />
        </Form.Group>

        <Form.Input
          name="email"
          placeholder="Correo electrónico"
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
          autoComplete="off"
        />

        <Form.Input
          type="password"
          name="password"
          placeholder="Contraseña"
          onChange={formik.handleChange}
          value={formik.values.password}
          error={formik.errors.password}
          autoComplete="new-password"
        />

        <Form.Field>
          <Checkbox
            toggle
            label="Activo"
            checked={formik.values.active}
            onChange={(_, data) => formik.setFieldValue("active", data.checked)}
          />
        </Form.Field>

        {/* Dropdown para seleccionar classnumber */}
        <Form.Field>
          <label>Selecciona la clase</label>
          <Dropdown
            placeholder="Selecciona una clase"
            fluid
            selection
            options={classNumberOptions}
            value={selectedClassnumber}
            onChange={(_, data) => setSelectedClassnumber(data.value)}
          />
        </Form.Field>

        {/* Dropdown para seleccionar el rol en la clase */}
        <Form.Field>
          <label>Selecciona el rol</label>
          <Dropdown
            placeholder="Selecciona un rol"
            fluid
            selection
            options={roleOptions}
            value={selectedRole}
            onChange={(_, data) => setSelectedRole(data.value)}
          />
        </Form.Field>

        <Button type="button" primary icon labelPosition="left" onClick={handleAddClassrole}>
          <Icon name="plus" />
          Añadir Rol a la Clase
        </Button>

        {classroleList.length > 0 && (
          <Card.Group>
            {classroleList.map((item, index) => (
              <Card key={index}>
                <Card.Content>
                  <Card.Header>{item.classnumber}</Card.Header>
                  <Card.Meta>{item.role}</Card.Meta>
                  <Button
                    icon="trash"
                    color="red"
                    size="small"
                    floated="right"
                    onClick={() => handleDeleteClassrole(index)}
                  />
                </Card.Content>
              </Card>
            ))}
          </Card.Group>
        )}

        <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
          {teacher ? "Actualizar Teacher" : "Crear Teacher"}
        </Form.Button>
      </Form>
    </>
  );
}
